import React from "react";
import { Auth0Provider } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0DeepLink } from "../hooks/useAuth0DeepLink.js";
import { IS_IOS_APP } from "../config/appTarget.js";
import { getRedirectUri } from "./authConfig.js";
import { SessionStorageCache } from "./sessionStorageCache.js";

/** Thin wrapper rendered inside Auth0Provider that activates the deep-link listener. */
function Auth0DeepLinkActivator() {
  useAuth0DeepLink();
  return null;
}

const Auth0ProviderWithHistory = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  if (!domain || !clientId) {
    console.error("Auth0 configuration missing:", {
      domain,
      clientId,
      audience,
    });
    return (
      <div>
        {t("auth0.configError")}
      </div>
    );
  }

  const onRedirectCallback = (
    appState?: {
      returnTo?: string;
    },
  ) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  const isNative = Capacitor.isNativePlatform() || IS_IOS_APP;
  const isLocalDevAuth =
    String(import.meta.env.VITE_REACT_APP_API_URL || "").includes("localhost");
  const isQaEnvironment = import.meta.env.VITE_APP_ENV === "qa";
  // Testing environments only: keep each browser tab's Auth0 session isolated
  // in sessionStorage so testers can be logged in as different accounts in
  // different tabs. Production keeps the shared `localstorage` cache so a
  // reload/new tab continues the same session.
  const isMultiAccountTesting = !isNative && (isLocalDevAuth || isQaEnvironment);
  const cacheLocation = "localstorage";
  const sessionCache = React.useMemo(
    () => (isMultiAccountTesting ? new SessionStorageCache() : undefined),
    [isMultiAccountTesting],
  );

  // On iOS, open Auth0 Universal Login in SFSafariViewController (via
  // @capacitor/browser) instead of the WKWebView. This is required because:
  //  - Google OAuth blocks logins inside embedded web views (WKWebView)
  //  - SFSafariViewController shares the system cookie jar (proper session)
  //  - The custom scheme redirect (com.pawseum.app://) is then caught by
  //    the appUrlOpen listener in useAuth0DeepLink
  // IS_IOS_APP is a build-time fallback so we never silently fall through to
  // window.location.assign on an iOS build even if Capacitor has a platform
  // detection race.
  const openUrl = isNative
    ? async (url: string) => {
        if (isLocalDevAuth) {
          console.info("[Auth0][native-dev] opening authorize URL", url);
        }
        try {
          const { Browser } = await import("@capacitor/browser");
          await Browser.open({ url, toolbarColor: "#6b2e8f", presentationStyle: "over" });
        } catch (err) {
          console.error(
            "[Auth0] Browser.open failed, falling back to window.location.assign",
            err,
          );
          window.location.assign(url);
        }
      }
    : undefined;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens
      // On native (Capacitor WKWebView) the iframe silent-auth fallback can
      // never complete — the Auth0 session cookie lives in SFSafariViewController,
      // not the WebView — so a failed refresh would hang ~60s on every request.
      // Also disable it for multi-account testing: the fallback re-authenticates
      // via Auth0's own SSO cookie, which is shared across every tab and would
      // silently swap the session to whichever account logged in most recently.
      useRefreshTokensFallback={!isNative && !isMultiAccountTesting}
      cacheLocation={cacheLocation}
      cache={sessionCache}
      authorizationParams={{
        redirect_uri: getRedirectUri(),
        audience: audience,
        scope: "openid profile email offline_access",
        // Force the login form instead of silently reusing Auth0's shared SSO
        // session, so a second tab can log in as a different test account.
        ...(isMultiAccountTesting ? { prompt: "login" } : {}),
      }}
      openUrl={openUrl}
    >
      <Auth0DeepLinkActivator />
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory;
